import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import PasswordInput from "@/components/PasswordInput";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { trackEvent } from "@/lib/analytics";
import { CheckCircle, Circle } from "@phosphor-icons/react";

const PW_RULES = [
  { key: "len", label: "11 caractères min.", test: (p) => p.length >= 11 },
  { key: "upper", label: "Une majuscule", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "Une minuscule", test: (p) => /[a-z]/.test(p) },
  { key: "digit", label: "Un chiffre", test: (p) => /\d/.test(p) },
  { key: "special", label: "Un caractère spécial", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const inputCls = "w-full bg-surface-2 border border-border rounded-md px-4 py-3 text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent/60 focus:border-accent/40 transition-shadow";

export default function Register() {
  const [form, setForm] = useState({ name: "", company: "", email: "", password: "", org_type: "ESN" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setOrgType = (v) => setForm((f) => ({ ...f, org_type: v }));
  const isFinalClient = form.org_type === "FINAL_CLIENT";

  const pwOk = PW_RULES.every((r) => r.test(form.password));

  const submit = async (e) => {
    e.preventDefault();
    if (!pwOk) { toast.error("Le mot de passe ne respecte pas les critères de sécurité."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      trackEvent("sign_up", { method: "email", org_type: form.org_type });
      login(data.token, data.user);
      toast.success("Compte créé");
      navigate(isFinalClient ? "/app/marketplace" : "/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const ACCOUNT_TYPES = [
    { v: "ESN", title: "Je suis une ESN / société de conseil", desc: "Je détecte des opportunités et je publie mes consultants disponibles." },
    { v: "FINAL_CLIENT", title: "Je suis une entreprise (client final)", desc: "Je recherche des consultants disponibles dans la Marketplace." },
  ];

  return (
    <AuthShell
      title="Créer votre compte"
      subtitle="Créez votre compte interKO, puis activez votre abonnement (29 €/mois, sans engagement) pour accéder immédiatement à la plateforme."
      footer={<>Déjà un compte ? <Link to="/login" className="text-accent hover:underline" data-testid="link-login">Se connecter</Link></>}
    >
      <GoogleAuthButton label="S'inscrire avec Google" />
      <p className="text-[11px] text-muted mt-2 text-center">Inscription en 1 clic — vous choisirez votre type de compte ensuite (ESN par défaut).</p>
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted">ou avec votre email</span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <label className="overline block mb-2">Type de compte</label>
          <div className="grid gap-2" data-testid="register-org-type">
            {ACCOUNT_TYPES.map((t) => {
              const active = form.org_type === t.v;
              return (
                <button type="button" key={t.v} onClick={() => setOrgType(t.v)}
                  data-testid={`register-org-type-${t.v}`}
                  className={`text-left rounded-md border px-4 py-3 transition-colors ${active ? "border-accent bg-accent/10" : "border-border bg-surface-2 hover:border-accent/40"}`}>
                  <div className="flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${active ? "border-accent" : "border-muted"}`}>
                      {active && <span className="w-2 h-2 rounded-full bg-accent" />}
                    </span>
                    <span className={`text-sm font-semibold ${active ? "text-fg" : "text-fg/80"}`}>{t.title}</span>
                  </div>
                  <p className="text-xs text-muted mt-1 ml-6">{t.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="overline block mb-2">Nom</label>
            <input required value={form.name} onChange={upd("name")} className={inputCls} placeholder="Marie Dupont" data-testid="register-name" />
          </div>
          <div>
            <label className="overline block mb-2">{isFinalClient ? "Entreprise" : "ESN / Société"}</label>
            <input value={form.company} onChange={upd("company")} className={inputCls} placeholder={isFinalClient ? "Votre entreprise" : "Votre société"} data-testid="register-company" />
          </div>
        </div>
        <div>
          <label className="overline block mb-2">Email professionnel</label>
          <input type="email" required value={form.email} onChange={upd("email")} className={inputCls} placeholder={isFinalClient ? "vous@entreprise.fr" : "vous@esn.fr"} data-testid="register-email" />
        </div>
        <div>
          <label className="overline block mb-2">Mot de passe</label>
          <PasswordInput value={form.password} onChange={upd("password")} required minLength={11} placeholder="11 caractères minimum" testid="register-password" />
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-3" data-testid="password-rules">
            {PW_RULES.map((r) => {
              const ok = r.test(form.password);
              return (
                <div key={r.key} className={`flex items-center gap-1.5 text-xs ${ok ? "text-accent" : "text-muted"}`}>
                  {ok ? <CheckCircle size={13} weight="fill" /> : <Circle size={13} />} {r.label}
                </div>
              );
            })}
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="register-submit"
          className="w-full bg-accent text-black font-semibold py-3 rounded-md hover-lift disabled:opacity-60">
          {loading ? "Création…" : "Créer mon compte"}
        </button>
      </form>
    </AuthShell>
  );
}
